// Learn Manager Class for the Learn Section
// Handles business logic and state management for learning content
// Follows the Manager pattern adapted from AgentGroupChat

import { Manager } from '../../../core';
import { logger } from '../../../core';

export class LearnManager extends Manager {
  constructor() {
    super();
    this.tutorialVideos = [];
    this.questionsAnswers = [];
    this.triviaQuestions = [];
    this.categories = [];
    this.featuredContent = {
      "tutorial-videos": { items: [] },
      "questions-answers": { items: [] },
      "trivia": { items: [] }
    };
    this.searchTerm = '';
    this.isLoading = false;
    this.currentSection = 'main'; // 'main', 'tutorial-videos', 'questions-answers', 'trivia'
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: 'manager|learn|init',
        color1: 'cyan',
      },
      'Initializing LearnSection manager states'
    );

    try {
      // Load initial content
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|learn|ready',
          color1: 'lime',
        },
        `LearnSection manager initialized with ${this.tutorialVideos.length} videos, ${this.questionsAnswers.length} Q&A items, ${this.triviaQuestions.length} trivia questions`
      );
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|error',
          color1: 'red',
          color2: 'orange',
          includeSource: true,
        },
        'Failed to initialize LearnSection manager states:',
        error
      );

      throw error;
    }
  }

  /**
   * Load learn content from backend or use placeholder data
   */
  async loadContent() {
    this.isLoading = true;

    try {
      logger.log(
        {
          tags: 'manager|learn|api',
          color1: 'blue',
        },
        'Loading learn content from API'
      );

      // Load categories from API
      const categoriesResponse = await fetch('/api/learn/categories');
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        this.categories = categoriesData.categories || [];
        logger.log(
          {
            tags: 'manager|learn|success',
            color1: 'lime',
          },
          `Loaded ${this.categories.length} categories from API`
        );
      } else {
        logger.warn(
          {
            tags: 'manager|learn|warning',
            color1: 'yellow',
          },
          'Failed to load categories from API, using empty array'
        );
        this.categories = [];
      }

      // Load tutorial videos from API
      const videosResponse = await fetch('/api/learn/videos?limit=1000');
      if (videosResponse.ok) {
        const videosData = await videosResponse.json();
        this.tutorialVideos = videosData.videos || [];
        logger.log(
          {
            tags: 'manager|learn|success',
            color1: 'lime',
          },
          `Loaded ${this.tutorialVideos.length} tutorial videos from API`
        );
      } else {
        logger.warn(
          {
            tags: 'manager|learn|warning',
            color1: 'yellow',
          },
          'Failed to load videos from API, using empty array'
        );
        this.tutorialVideos = [];
      }

      // Load featured content from API
      const featuredResponse = await fetch('/api/learn/featured');
      if (featuredResponse.ok) {
        const featuredData = await featuredResponse.json();
        this.featuredContent = featuredData.featured || {
          "tutorial-videos": { items: [] },
          "questions-answers": { items: [] },
          "trivia": { items: [] }
        };
        logger.log(
          {
            tags: 'manager|learn|success',
            color1: 'lime',
          },
          `Loaded featured content from API`
        );
      } else {
        logger.warn(
          {
            tags: 'manager|learn|warning',
            color1: 'yellow',
          },
          'Failed to load featured content from API, using defaults'
        );
        // Keep default empty featured content
      }

      // Load Q&A from API
      const qaResponse = await fetch('/api/learn/questions?limit=1000');
      if (qaResponse.ok) {
        const qaData = await qaResponse.json();
        // Map API response to match component expectations
        this.questionsAnswers = (qaData.qa || []).map(qa => ({
          id: qa.id,
          question: qa.question,
          answer: qa.answer,
          category: qa.categoryName || 'Uncategorized', // Map categoryName to category
          helpful: qa.likes || 0, // Map likes to helpful
          userLiked: qa.userLiked || false, // Track if user liked this item
          createdAt: qa.createdAt,
          tags: qa.tags || [], // Include tags for tag-based searching
          links: qa.links || [] // Include links for reference materials
        }));
        logger.log(
          {
            tags: 'manager|learn|success',
            color1: 'lime',
          },
          `Loaded ${this.questionsAnswers.length} Q&A items from API`
        );
      } else {
        logger.warn(
          {
            tags: 'manager|learn|warning',
            color1: 'yellow',
          },
          'Failed to load Q&A from API, using placeholder data'
        );
        this.questionsAnswers = this.getPlaceholderQuestionsAnswers();
      }

      // Load trivia from API
      const triviaResponse = await fetch('/api/learn/trivia?limit=1000');
      if (triviaResponse.ok) {
        const triviaData = await triviaResponse.json();
        this.triviaQuestions = triviaData.trivia || [];
        logger.log(
          {
            tags: 'manager|learn|success',
            color1: 'lime',
          },
          `Loaded ${this.triviaQuestions.length} trivia questions from API`
        );
      } else {
        logger.warn(
          {
            tags: 'manager|learn|warning',
            color1: 'yellow',
          },
          'Failed to load trivia from API, using placeholder data'
        );
        this.triviaQuestions = this.getPlaceholderTriviaQuestions();
      }

    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|error',
          color1: 'red',
        },
        'Failed to load learn content:',
        error
      );
      // Fallback to empty arrays on error
      this.tutorialVideos = [];
      this.questionsAnswers = [];
      this.triviaQuestions = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get featured tutorial videos (full video objects)
   */
  getFeaturedTutorialVideos() {
    const featuredItems = this.featuredContent["tutorial-videos"]?.items || [];

    // Return full video objects for featured items (only if explicitly featured)
    return featuredItems
      .map(item => this.tutorialVideos.find(video => video.id === item.id))
      .filter(video => video !== undefined); // Filter out any videos that couldn't be found
  }

  getFeaturedQuestionsAnswers() {
    const featuredItems = this.featuredContent["questions-answers"]?.items || [];

    // Return full Q&A objects for featured items (only if explicitly featured)
    return featuredItems
      .map(item => this.questionsAnswers.find(qa => qa.id === item.id))
      .filter(qa => qa !== undefined); // Filter out any Q&A items that couldn't be found
  }

  getFeaturedTriviaQuestions() {
    const featuredItems = this.featuredContent["trivia"]?.items || [];

    // Return full trivia objects for featured items (only if explicitly featured)
    return featuredItems
      .map(item => this.triviaQuestions.find(trivia => trivia.id === item.id))
      .filter(trivia => trivia !== undefined); // Filter out any trivia items that couldn't be found
  }

  /**
   * Set search term and update filtered results
   */
  setSearchTerm(term) {
    this.searchTerm = term;

    logger.log(
      {
        tags: 'manager|learn|action',
        color1: 'blue',
      },
      `Search term updated: "${term}"`
    );
  }

  /**
   * Get filtered tutorial videos based on search term and category
   */
  getVideosByCategory(categoryId) {
    if (!categoryId || categoryId === 'all') {
      return this.tutorialVideos;
    }
    return this.tutorialVideos.filter(video => video.categoryId === categoryId);
  }

  getFilteredTutorialVideos(categoryId) {
    // If no category specified, use the first category or show all
    if (!categoryId || categoryId === 'all') {
      return this.tutorialVideos;
    }

    // Filter by categoryId (real database category ID)
    return this.tutorialVideos.filter(video => video.categoryId === categoryId);
  }

  /**
   * Resolve a single Q&A by id (string or ObjectId string from API).
   */
  getQuestionAnswerById(id) {
    if (id === undefined || id === null || id === '') return null;
    const key = String(id);
    return this.questionsAnswers.find((qa) => String(qa.id) === key) || null;
  }

  /**
   * Get filtered Q&A items based on search term
   * - Regular search: searches question titles only
   * - #tag search: searches tags (e.g., "#general" searches for tag "general")
   */
  getFilteredQuestionsAnswers() {
    const search = this.searchTerm.trim();

    if (!search) {
      return this.questionsAnswers;
    }

    const searchLower = search.toLowerCase();

    // Check if this is a tag search (starts with #)
    if (search.startsWith('#')) {
      const tagSearch = searchLower.substring(1); // Remove the #
      return this.questionsAnswers.filter(qa =>
        qa.tags && qa.tags.some(tag => tag.toLowerCase().includes(tagSearch))
      );
    }

    // Regular search: only search question titles
    return this.questionsAnswers.filter(qa =>
      qa.question.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Toggle like status for a Q&A item
   */
  async toggleLikeQA(qaId) {
    try {
      logger.log(
        {
          tags: 'manager|learn|toggle-like',
          color1: 'cyan',
        },
        `Toggling like for Q&A: ${qaId}`
      );

      const response = await fetch(`/api/learn/questions/${qaId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to toggle like');
      }

      // Update the Q&A in our local state to reflect the new like count and status
      const qaIndex = this.questionsAnswers.findIndex(qa => qa.id === qaId);
      if (qaIndex !== -1) {
        this.questionsAnswers[qaIndex].helpful = data.likes;
        this.questionsAnswers[qaIndex].userLiked = data.liked;
      }

      return {
        likes: data.likes,
        liked: data.liked
      };
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|toggle-like|error',
          color1: 'red',
        },
        `Failed to toggle like for Q&A ${qaId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get all filtered content combined
   */
  getAllFilteredContent() {
    return {
      tutorialVideos: this.getFilteredTutorialVideos(),
      questionsAnswers: this.getFilteredQuestionsAnswers(),
      triviaQuestions: this.getFilteredTriviaQuestions(),
    };
  }

  /**
   * Set current section (for subpage navigation)
   */
  setCurrentSection(section) {
    this.currentSection = section;

    logger.log(
      {
        tags: 'manager|learn|navigation',
        color1: 'yellow',
      },
      `Current section set to: ${section}`
    );
  }

  /**
   * Create a new tutorial video
   */
  async createTutorialVideo(videoData) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api',
          color1: 'blue',
        },
        'Creating tutorial video:', videoData.name
      );

      const response = await fetch('/api/learn/admin/videos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoData),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create video');
      }

      // Reload content to get updated list
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|learn|success',
          color1: 'lime',
        },
        'Tutorial video created successfully:', data.video.id
      );

      return data.video;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|error',
          color1: 'red',
        },
        'Failed to create tutorial video:', error
      );
      throw error;
    }
  }

  /**
   * Update an existing tutorial video
   */
  async updateTutorialVideo(videoId, videoData) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api',
          color1: 'blue',
        },
        'Updating tutorial video:', videoId
      );

      const response = await fetch(`/api/learn/admin/videos/${videoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoData),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update video');
      }

      // Reload content to get updated list
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|learn|success',
          color1: 'lime',
        },
        'Tutorial video updated successfully:', videoId
      );

      return data.video;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|error',
          color1: 'red',
        },
        'Failed to update tutorial video:', error
      );
      throw error;
    }
  }

  /**
   * Delete a tutorial video
   */
  async deleteTutorialVideo(videoId) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api',
          color1: 'blue',
        },
        'Deleting tutorial video:', videoId
      );

      const response = await fetch(`/api/learn/admin/videos/${videoId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete video');
      }

      // Reload content to get updated list
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|learn|success',
          color1: 'lime',
        },
        'Tutorial video deleted successfully:', videoId
      );

      return true;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|error',
          color1: 'red',
        },
        'Failed to delete tutorial video:', error
      );
      throw error;
    }
  }

  /**
   * Placeholder tutorial videos data - 15 getting started videos for testing
   */
  getPlaceholderTutorialVideos() {
    return [
      // Randomly scattered: mix of panels and cards throughout
      {
        id: 'creating-your-account',
        title: 'Creating Your Account',
        description: 'Step-by-step guide to setting up your Revolt account and getting started.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Account+Setup',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder2',
        duration: '4:22',
        views: 1890,
        tags: ['getting-started', 'basics', 'account'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-16T11:30:00Z',
      },
      {
        id: 'welcome-to-revolt',
        title: 'Welcome to Revolt',
        description: 'An introduction to Revolt\'s AI-powered development platform and what makes it unique.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Welcome',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder1',
        duration: '3:15',
        views: 2450,
        tags: ['getting-started', 'basics', 'introduction'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-15T10:00:00Z',
      },
      {
        id: 'your-first-project',
        title: 'Creating Your First Project',
        description: 'Learn how to create and set up your very first project in Revolt.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=First+Project',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder4',
        duration: '5:30',
        views: 2100,
        tags: ['getting-started', 'basics', 'projects', 'first-app'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-18T09:45:00Z',
      },
      {
        id: 'revolt-interface-tour',
        title: 'Revolt Interface Tour',
        description: 'Explore the main interface components and navigation of the Revolt platform.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Interface+Tour',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder3',
        duration: '6:45',
        views: 1650,
        tags: ['getting-started', 'basics', 'interface', 'navigation'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-17T14:15:00Z',
      },
      {
        id: 'ai-chat-basics',
        title: 'AI Chat Basics',
        description: 'Introduction to using the AI chat feature for help and guidance.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=AI+Chat+Basics',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder6',
        duration: '4:58',
        views: 1950,
        tags: ['getting-started', 'ai', 'chat', 'basics'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-20T10:10:00Z',
      },
      {
        id: 'understanding-workspace',
        title: 'Understanding the Workspace',
        description: 'Get familiar with the workspace layout, file explorer, and main editing area.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Workspace',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder5',
        duration: '7:12',
        views: 1780,
        tags: ['getting-started', 'workspace', 'basics'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-19T13:20:00Z',
      },
      {
        id: 'basic-coding-concepts',
        title: 'Basic Coding Concepts',
        description: 'Understand fundamental programming concepts used in Revolt development.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Coding+Basics',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder8',
        duration: '8:20',
        views: 1680,
        tags: ['getting-started', 'coding', 'basics', 'programming'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-22T11:45:00Z',
      },
      {
        id: 'file-management',
        title: 'File Management in Revolt',
        description: 'Learn how to organize, upload, and manage files in your projects.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=File+Management',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder7',
        duration: '5:45',
        views: 1420,
        tags: ['getting-started', 'files', 'basics', 'workspace'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-21T15:30:00Z',
      },
      {
        id: 'saving-and-sharing',
        title: 'Saving and Sharing Projects',
        description: 'Learn how to save your work and share projects with others.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Saving+Sharing',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder10',
        duration: '5:10',
        views: 1520,
        tags: ['getting-started', 'saving', 'sharing', 'projects'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-24T09:30:00Z',
      },
      {
        id: 'running-your-app',
        title: 'Running Your App',
        description: 'How to run, preview, and test your applications in the Revolt environment.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Running+Apps',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder9',
        duration: '4:35',
        views: 1870,
        tags: ['getting-started', 'running', 'preview', 'testing'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-23T14:00:00Z',
      },
      {
        id: 'troubleshooting-common-issues',
        title: 'Troubleshooting Common Issues',
        description: 'Solutions to frequently encountered problems and error messages.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Troubleshooting',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder12',
        duration: '7:30',
        views: 1690,
        tags: ['getting-started', 'troubleshooting', 'errors', 'basics'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-26T12:45:00Z',
      },
      {
        id: 'keyboard-shortcuts',
        title: 'Essential Keyboard Shortcuts',
        description: 'Boost your productivity with Revolt\'s most useful keyboard shortcuts.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Keyboard+Shortcuts',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder11',
        duration: '6:05',
        views: 1340,
        tags: ['getting-started', 'shortcuts', 'productivity', 'basics'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-25T16:15:00Z',
      },
      {
        id: 'next-steps',
        title: 'What\'s Next After Getting Started',
        description: 'Overview of advanced features and where to go from here.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Next+Steps',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder14',
        duration: '4:15',
        views: 1450,
        tags: ['getting-started', 'next-steps', 'advanced', 'overview'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-28T13:10:00Z',
      },
      {
        id: 'getting-help',
        title: 'Getting Help and Support',
        description: 'How to access documentation, community support, and additional resources.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Getting+Help',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder13',
        duration: '3:50',
        views: 1230,
        tags: ['getting-started', 'help', 'support', 'resources'],
        layout: 'panel', // 2x1 layout
        createdAt: '2024-01-27T10:20:00Z',
      },
      {
        id: 'best-practices',
        title: 'Best Practices for Beginners',
        description: 'Tips and recommendations for developing good habits in Revolt.',
        thumbnailUrl: 'https://via.placeholder.com/320x180/8b0000/ffffff?text=Best+Practices',
        videoUrl: 'https://www.youtube.com/watch?v=placeholder15',
        duration: '6:40',
        views: 1580,
        tags: ['getting-started', 'best-practices', 'tips', 'habits'],
        layout: 'card', // 1x1 layout
        createdAt: '2024-01-29T11:55:00Z',
      },
    ];
  }

  /**
   * Placeholder Q&A data
   */
  getPlaceholderQuestionsAnswers() {
    return [
      {
        id: 'what-is-revolt',
        question: 'What is Revolt?',
        answer: 'Revolt is an AI-powered web development platform that allows you to create, deploy, and manage web applications using natural language instructions and visual development tools.',
        category: 'general',
        helpful: 45,
        createdAt: '2024-01-10T08:00:00Z',
        tags: ['introduction', 'overview', 'platform'],
      },
      {
        id: 'how-ai-works',
        question: 'How does the AI development system work?',
        answer: 'Revolt\'s AI analyzes your requirements and generates code, UI components, and configurations automatically. You can then refine and customize the generated code using our visual editor and chat interface.',
        category: 'ai',
        helpful: 32,
        createdAt: '2024-01-12T11:20:00Z',
        tags: ['ai', 'development', 'code-generation'],
      },
      {
        id: 'free-tier-limits',
        question: 'What are the limits for free tier users?',
        answer: 'Free users can create up to 5 apps, have limited AI credits, and can only make public projects. Upgrade to Pro or Max plans for unlimited apps and premium features.',
        category: 'billing',
        helpful: 28,
        createdAt: '2024-01-18T16:45:00Z',
        tags: ['free', 'limits', 'billing', 'pricing'],
      },
    ];
  }

  /**
   * Placeholder trivia data
   */
  getPlaceholderTriviaQuestions() {
    return [
      {
        id: 'first-app-question',
        question: 'What was the first app template available in Revolt?',
        options: ['P5.js Demo', 'ChatBot Assistant', 'E-commerce Website', 'Portfolio Site'],
        correctAnswer: 0,
        explanation: 'The P5.js Demo was the original starting template that introduced users to Revolt\'s visual programming capabilities.',
        category: 'history',
        difficulty: 'easy',
        createdAt: '2024-01-08T13:30:00Z',
      },
      {
        id: 'ai-models-question',
        question: 'Which AI model powers Revolt\'s code generation?',
        options: ['GPT-3', 'Claude', 'Grok', 'Custom Revolt AI'],
        correctAnswer: 2,
        explanation: 'Revolt uses Grok, built by xAI, as its primary AI model for code generation and development assistance.',
        category: 'technical',
        difficulty: 'medium',
        createdAt: '2024-01-14T10:15:00Z',
      },
    ];
  }

  /**
   * Get individual video data with user like status
   */
  async getVideoWithLikeStatus(videoId) {
    try {
      logger.log(
        {
          tags: 'manager|learn|get-video',
          color1: 'blue',
        },
        `Fetching individual video data for: ${videoId}`
      );

      const response = await fetch(`/api/learn/videos/${videoId}`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch video');
      }

      // Update the video in our local cache with the fresh data
      const videoIndex = this.tutorialVideos.findIndex(v => v.id === videoId);
      if (videoIndex !== -1) {
        this.tutorialVideos[videoIndex] = { ...this.tutorialVideos[videoIndex], ...data.video };
      }

      logger.log(
        {
          tags: 'manager|learn|get-video|success',
          color1: 'lime',
        },
        `Successfully fetched video ${videoId} with userLiked: ${data.video.userLiked}`
      );

      return data.video;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|get-video|error',
          color1: 'red',
        },
        `Failed to fetch video ${videoId}:`, error
      );
      throw error;
    }
  }

  /**
   * Toggle like status for a video
   */
  async toggleLikeVideo(videoId) {
    try {
      logger.log(
        {
          tags: 'manager|learn|toggle-like',
          color1: 'cyan',
        },
        `Toggling like for video: ${videoId}`
      );

      const response = await fetch(`/api/learn/videos/${videoId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to toggle like');
      }

      // Update the video in our local state to reflect the new like count and status
      const videoIndex = this.tutorialVideos.findIndex(v => v.id === videoId);
      if (videoIndex !== -1) {
        this.tutorialVideos[videoIndex].likes = data.likes;
        this.tutorialVideos[videoIndex].userLiked = data.liked;
      }

      logger.log(
        {
          tags: 'manager|learn|toggle-like|success',
          color1: 'lime',
        },
        `Successfully toggled like for video ${videoId}, liked: ${data.liked}, count: ${data.likes}`
      );

      return data;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|toggle-like|error',
          color1: 'red',
        },
        `Failed to toggle like for video ${videoId}:`, error
      );
      throw error;
    }
  }

  /**
   * Toggle like/unlike for a trivia question
   */
  async toggleLikeTrivia(triviaId) {
    try {
      logger.log(
        {
          tags: 'manager|learn|toggle-like',
          color1: 'cyan',
        },
        `Toggling like for trivia: ${triviaId}`
      );

      const response = await fetch(`/api/learn/trivia/${triviaId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to toggle like');
      }

      // Update the trivia in our local state to reflect the new like count and status
      const triviaIndex = this.triviaQuestions.findIndex(t => t.id === triviaId);
      if (triviaIndex !== -1) {
        this.triviaQuestions[triviaIndex].likes = data.likes;
        this.triviaQuestions[triviaIndex].userLiked = data.liked;
      }

      logger.log(
        {
          tags: 'manager|learn|toggle-like|success',
          color1: 'lime',
        },
        `Successfully toggled like for trivia ${triviaId}, liked: ${data.liked}, count: ${data.likes}`
      );

      return data;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|toggle-like|error',
          color1: 'red',
        },
        `Failed to toggle like for trivia ${triviaId}:`, error
      );
      throw error;
    }
  }

  /**
   * Check if user liked a video
   */
  async checkVideoLikeStatus(videoId) {
    try {
      const response = await fetch(`/api/learn/videos/${videoId}/like`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to check like status');
      }

      // Update the video in our local state
      const videoIndex = this.tutorialVideos.findIndex(v => v.id === videoId);
      if (videoIndex !== -1) {
        this.tutorialVideos[videoIndex].userLiked = data.liked;
      }

      return data.liked;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|like|status|error',
          color1: 'red',
        },
        `Failed to check like status for video ${videoId}:`, error
      );
      return false; // Default to not liked on error
    }
  }

  /**
   * Check if user liked a trivia question
   */
  async checkTriviaLikeStatus(triviaId) {
    try {
      const response = await fetch(`/api/learn/trivia/${triviaId}/like`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to check like status');
      }

      // Update the trivia in our local state
      const triviaIndex = this.triviaQuestions.findIndex(t => t.id === triviaId);
      if (triviaIndex !== -1) {
        this.triviaQuestions[triviaIndex].userLiked = data.liked;
      }

      return data.liked;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|like|status|error',
          color1: 'red',
        },
        `Failed to check like status for trivia ${triviaId}:`, error
      );
      return false; // Default to not liked on error
    }
  }

  /**
   * Create a new trivia question (admin only)
   */
  async createTriviaQuestion(triviaData) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api|create',
          color1: 'blue',
        },
        'Creating trivia question:', triviaData.question
      );

      const response = await fetch('/api/learn/admin/trivia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(triviaData)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create trivia question');
      }

      // Reload trivia content to include the new question
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|learn|create|success',
          color1: 'lime',
        },
        `Successfully created trivia question: ${data.trivia.id}`
      );

      return data.trivia;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|create|error',
          color1: 'red',
        },
        'Failed to create trivia question:', error
      );
      throw error;
    }
  }

  /**
   * Update a trivia question (admin only)
   */
  async updateTriviaQuestion(triviaId, triviaData) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api|update',
          color1: 'blue',
        },
        `Updating trivia question: ${triviaId}`
      );

      const response = await fetch(`/api/learn/admin/trivia/${triviaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(triviaData)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update trivia question');
      }

      // Reload trivia content to reflect the changes
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|learn|update|success',
          color1: 'lime',
        },
        `Successfully updated trivia question: ${triviaId}`
      );

      return data.trivia;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|update|error',
          color1: 'red',
        },
        `Failed to update trivia question ${triviaId}:`, error
      );
      throw error;
    }
  }

  /**
   * Delete a trivia question (admin only)
   */
  async deleteTriviaQuestion(triviaId) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api|delete',
          color1: 'blue',
        },
        `Deleting trivia question: ${triviaId}`
      );

      const response = await fetch(`/api/learn/admin/trivia/${triviaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete trivia question');
      }

      // Remove from local state
      this.triviaQuestions = this.triviaQuestions.filter(q => q.id !== triviaId);

      logger.log(
        {
          tags: 'manager|learn|delete|success',
          color1: 'lime',
        },
        `Successfully deleted trivia question: ${triviaId}`
      );

      return data;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|delete|error',
          color1: 'red',
        },
        `Failed to delete trivia question ${triviaId}:`, error
      );
      throw error;
    }
  }

  /**
   * Get filtered trivia questions based on search term, type, and difficulty
   */
  getFilteredTriviaQuestions(search = '', type = 'all', difficulty = 'all') {
    let filtered = [...this.triviaQuestions];

    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      filtered = filtered.filter(trivia =>
        trivia.question.toLowerCase().includes(searchLower) ||
        trivia.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Apply type filter
    if (type && type !== 'all') {
      filtered = filtered.filter(trivia => trivia.questionType === type);
    }

    // Apply difficulty filter
    if (difficulty && difficulty !== 'all') {
      filtered = filtered.filter(trivia => trivia.difficulty === difficulty);
    }

    return filtered;
  }

  /**
   * Get trivia questions by type
   */
  getTriviaByType(type) {
    if (!type || type === 'all') {
      return this.triviaQuestions;
    }
    return this.triviaQuestions.filter(trivia => trivia.questionType === type);
  }

  /**
   * Get trivia questions by difficulty
   */
  getTriviaByDifficulty(difficulty) {
    if (!difficulty || difficulty === 'all') {
      return this.triviaQuestions;
    }
    return this.triviaQuestions.filter(trivia => trivia.difficulty === difficulty);
  }

  /**
   * Get trivia questions by category
   */
  getTriviaByCategory(categoryId) {
    if (!categoryId || categoryId === 'all') {
      return this.triviaQuestions;
    }
    return this.triviaQuestions.filter(trivia => trivia.categoryId === categoryId);
  }

  /**
   * Submit answer for a trivia question
   */
  async submitTriviaAnswer(triviaId, answer) {
    try {
      logger.log(
        {
          tags: 'manager|learn|api|submit-answer',
          color1: 'blue',
        },
        `Submitting answer for trivia question: ${triviaId}`
      );

      const response = await fetch(`/api/learn/trivia/${triviaId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ answer })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit answer');
      }

      logger.log(
        {
          tags: 'manager|learn|submit-answer|success',
          color1: 'lime',
        },
        `Successfully submitted answer for trivia ${triviaId}, correct: ${data.correct}`
      );

      return data;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|learn|submit-answer|error',
          color1: 'red',
        },
        `Failed to submit answer for trivia ${triviaId}:`, error
      );
      throw error;
    }
  }
}